import { css } from 'aphrodite';
import { ReactNode, useEffect, useRef } from 'react';
import type { KeyboardEvent } from 'react';
import { Button } from '../../../web/components-v2/Button';
import { XIcon } from '../../../web/components/svgs/icons/XIcon';
import { s } from './styles';

export interface PaymentPageProps {
	title: string;
	onClose: () => void;
	summaryContent: ReactNode;
	children: ReactNode;
}

/**
 * PaymentPage is a presentational layout component for payment flows.
 * It renders a full-page layout with a branded header, a summary column (left),
 * and a payment column (right) for checkout content.
 * Positions itself below any custom header present on the survey page.
 */
export function PaymentPage({ title, onClose, summaryContent, children }: PaymentPageProps) {
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const previouslyFocused = document.activeElement as HTMLElement | null;
		containerRef.current?.focus();

		return () => {
			previouslyFocused?.focus();
		};
	}, []);

	useEffect(() => {
		const customHeader = document.querySelector('[data-survey-header]') as HTMLElement | null;
		if (!customHeader) {
			if (containerRef.current) {
				containerRef.current.style.top = '0px';
			}
			return;
		}

		customHeader.style.position = 'fixed';
		customHeader.style.top = '0';
		customHeader.style.left = '0';
		customHeader.style.right = '0';
		customHeader.style.zIndex = '11';

		const observer = new ResizeObserver(entries => {
			if (containerRef.current) {
				containerRef.current.style.top = `${entries[0].contentRect.height}px`;
			}
		});
		observer.observe(customHeader);

		return () => {
			observer.disconnect();
			customHeader.style.position = '';
			customHeader.style.top = '';
			customHeader.style.left = '';
			customHeader.style.right = '';
			customHeader.style.zIndex = '';
		};
	}, []);

	const handleKeyDown = (e: KeyboardEvent) => {
		if (e.key !== 'Tab') return;

		const focusable = containerRef.current?.querySelectorAll<HTMLElement>(
			'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);
		if (!focusable || focusable.length === 0) return;

		const first = focusable[0];
		const last = focusable[focusable.length - 1];

		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	};

	return (
		<div
			ref={containerRef}
			tabIndex={-1}
			className={css(s.pageContainer)}
			role='dialog'
			aria-modal='true'
			aria-labelledby='payment-page-title'
			onKeyDown={handleKeyDown}
		>
			<header className={css(s.header)}>
				<h1 id='payment-page-title' className={css(s.headerTitle)}>
					{title}
				</h1>
				<Button type='button' variant='link' aria-label='Close' onClick={onClose} styles={[s.closeButton]}>
					<XIcon />
				</Button>
			</header>
			<div className={css(s.scrollArea)}>
				<div className={css(s.contentContainer)}>
					<div className={css(s.summaryColumn)}>{summaryContent}</div>
					<div className={css(s.paymentColumn)}>{children}</div>
				</div>
			</div>
		</div>
	);
}
